import axios from 'axios';

// Create axios instance with optimized configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api',
  timeout: 30000, // Reduced timeout to 30 seconds for better UX
  headers: {
    'Content-Type': 'application/json',
  },
  // Add request caching and retry logic
  validateStatus: (status) => status < 500, // Don't retry 4xx errors
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    // Try customer token first, then fallback to regular token
    const customerToken = localStorage.getItem('customerToken');
    const token = localStorage.getItem('token');
    const authToken = customerToken || token;
    
    
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
  //    console.log('🔐 [API REQUEST] Adding auth token to request:', config.url);
    } else {
      console.warn('⚠️ [API REQUEST] No auth token found for request:', config.url);
      // For debugging - show what tokens we tried to find
   //   console.log('🔍 [API REQUEST] Tokens checked - customerToken:', !!customerToken, 'token:', !!token);
    }
    
    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with enhanced error handling
api.interceptors.response.use(
  (response) => {
    // Log response time for performance monitoring
    const endTime = new Date();
    const duration = endTime - response.config.metadata.startTime;

    
    return response;
  },
  (error) => {
    // Enhanced error handling with detailed logging
    if (error.response) {
      const { status, config, data } = error.response;
      const url = config?.url || 'unknown endpoint';
      
      if (status === 401) {
        console.error(`🔒 Authentication error (401) for ${url}`);
        
        // Check if this is a token expiration error
        const errorMessage = data?.message || data?.error || '';
        const isTokenExpired = errorMessage && (
          errorMessage.toLowerCase().includes('token expired') ||
          errorMessage.toLowerCase().includes('jwt expired') ||
          errorMessage.toLowerCase().includes('expired token')
        );
        
        if (isTokenExpired) {
          // Token is expired - clear it and redirect to login
          console.error('❌ Token has expired - clearing tokens and redirecting to login');
          localStorage.removeItem('customerToken');
          localStorage.removeItem('token');
          localStorage.removeItem('tempCustomerToken');
          localStorage.removeItem('user');
          
          // Dispatch token expiration event
          window.dispatchEvent(new CustomEvent('auth:token-expired', {
            detail: { 
              reason: 'token_expired',
              message: 'Your session has expired. Please log in again.'
            }
          }));
          
          // Redirect to login after a short delay
          setTimeout(() => {
            window.location.href = '/login';
          }, 500);
        } else {
          // Other 401 error - preserve token (might be temporary)
          console.warn('⚠️ Authentication error - token preserved (may be temporary issue)');
        }
      } else if (status === 404) {
        console.error(`🔍 Resource not found (404) for ${url} - The requested data may not exist`);
        // We don't use fallbacks as requested
      } else if (status === 400) {
        console.error(`❌ Bad request (400) for ${url} - Request data may be invalid`, error.response.data);
      } else {
        console.error(`⚠️ API Error: ${status} for ${url}`, error.response.data);
      }
    } else if (error.code === 'ECONNABORTED') {
      console.error('⏰ Request timeout - server may be overloaded');
    } else if (error.code === 'ERR_NETWORK') {
      console.error('🌐 Network error - check internet connection');
    } else {
      console.error('🔥 Unknown API error:', error);
    }
    
    return Promise.reject(error);
  }
);

// Simple in-memory cache for API responses
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedResponse = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedResponse = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Clear cache when user logs out
export const clearCache = () => {
  cache.clear();
};

// Auth API methods
export const authAPI = {
  // Customer signup
  customerSignup: async (userData) => {
    try {
      const response = await api.post('/auth/customer/signup', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Signup failed' };
    }
  },

  customerSignupWithPhoto: async (formData) => {
    try {
      const response = await api.post('/auth/customer/signup-with-photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Signup with photo failed' };
    }
  },

  // Customer login
  customerLogin: async (identifier, password) => {
    try {
      const response = await api.post('/auth/customer/login', { identifier, password });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Login failed' };
    }
  },

  // Customer logout
  customerLogout: async () => {
    try {
      const response = await api.post('/auth/customer/logout');
      localStorage.removeItem('customerToken');
      localStorage.removeItem('user');
      return response.data;
    } catch (error) {
      localStorage.removeItem('customerToken');
      localStorage.removeItem('user');
      throw error.response?.data || { error: 'Logout failed' };
    }
  },

  // Verify email
  verifyEmail: async (token) => {
    try {
      const response = await api.post('/auth/customer/verify-email', { token });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Email verification failed' };
    }
  },

  // Forgot password
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/customer/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Password reset failed' };
    }
  },

  // Reset password
  resetPassword: async (token, newPassword) => {
    try {
      const response = await api.post('/auth/customer/reset-password', { token, newPassword });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Password reset failed' };
    }
  },

  // Change password
  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/auth/customer/change-password', { currentPassword, newPassword });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Password change failed' };
    }
  },

  // Get customer profile
  getCustomerProfile: async () => {
    try {
      const response = await api.get('/auth/customer/profile');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get profile' };
    }
  },

  // Alias for getCustomerProfile for consistency
  getProfile: async () => {
    return authAPI.getCustomerProfile();
  },

  // Update customer profile
  updateCustomerProfile: async (profileData) => {
    try {
      const response = await api.put('/customer/profile', profileData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to update profile' };
    }
  },

  // Update profile photo
  updateProfilePhoto: async (formData) => {
    try {
      const response = await api.post('/customer/profile/photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to update profile photo' };
    }
  },

  // Alias for updateCustomerProfile for consistency
  updateProfile: async (profileData) => {
    return authAPI.updateCustomerProfile(profileData);
  }
};

// Home API methods
export const homeAPI = {
  // Get offers with caching
  getOffers: async () => {
    const cacheKey = 'home_offers';
    const cached = getCachedResponse(cacheKey);
    if (cached) {
   //   console.log('📦 Using cached offers data');
      return cached;
    }

    try {
  //    console.log('🔍 Fetching offers from API...');
      const response = await api.get('/customer/home/offers');
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching offers:', error);
      throw error.response?.data || { error: 'Failed to get offers' };
    }
  },

  // Get offers by type with caching
  getOffersByType: async (offerType) => {
    const cacheKey = `home_offers_${offerType}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
   //   console.log('📦 Using cached offers by type data');
      return cached;
    }

    try {
 //     console.log(`🔍 Fetching offers by type ${offerType} from API...`);
      const response = await api.get('/customer/home/offers/by-type', { params: { offerType } });
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching offers by type:', error);
      throw error.response?.data || { error: 'Failed to get offers by type' };
    }
  },

  // Get featured services with caching
  getFeaturedServices: async (params = {}) => {
    const cacheKey = `home_featured_services_${JSON.stringify(params)}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      console.log('📦 Using cached featured services data');
      return cached;
    }

    try {
      console.log('🔍 Fetching featured services from API...');
      const response = await api.get('/customer/home/services/featured', { params });
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching featured services:', error);
      throw error.response?.data || { error: 'Failed to get featured services' };
    }
  },

  // Get featured providers with caching
  getFeaturedProviders: async (params = {}) => {
    const cacheKey = `home_featured_providers_${JSON.stringify(params)}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      console.log('📦 Using cached featured providers data');
      return cached;
    }

    try {
  //    console.log('🔍 Fetching featured providers from API...');
      const response = await api.get('/customer/home/providers/featured', { params });
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching featured providers:', error);
      throw error.response?.data || { error: 'Failed to get featured providers' };
    }
  },

  // Get categories with caching
  getCategories: async () => {
    const cacheKey = 'home_categories';
    const cached = getCachedResponse(cacheKey);
    if (cached) {
  //    console.log('📦 Using cached categories data');
      return cached;
    }

    try {
  //    console.log('🔍 Fetching categories from API...');
      const response = await api.get('/customer/home/categories');
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error.response?.data || { error: 'Failed to get categories' };
    }
  },

  // Get products with caching
  getProducts: async (params = {}) => {
    const cacheKey = `home_products_${JSON.stringify(params)}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      console.log('📦 Using cached products data');
      return cached;
    }

    try {
      console.log('🔍 Fetching products from API...');
      const response = await api.get('/customer/home/products/popular', { params });
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error.response?.data || { error: 'Failed to get products' };
    }
  },

  // Get banners with caching
  getBanners: async () => {
    const cacheKey = 'home_banners';
    const cached = getCachedResponse(cacheKey);
    if (cached) {
   //   console.log('📦 Using cached banners data');
      return cached;
    }

    try {
   //   console.log('🔍 Fetching banners from API...');
      const response = await api.get('/customer/home/banners');
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching banners:', error);
      throw error.response?.data || { error: 'Failed to get banners' };
    }
  },

  // Get all home data in one call (ULTRA OPTIMIZED)
  getAllData: async (params = {}) => {
    const cacheKey = `home_all_data_${JSON.stringify(params)}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      console.log('📦 Using cached all home data');
      return cached;
    }

    try {
      console.log('🚀 Fetching all home data from consolidated endpoint...');
      const response = await api.get('/customer/home/all-data', { params });
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching all home data:', error);
      throw error.response?.data || { error: 'Failed to get all home data' };
    }
  }
};

// Provider API methods
export const providerAPI = {
  // Search providers with caching
  searchProviders: async (params) => {
    const cacheKey = `provider_search_${JSON.stringify(params)}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      console.log('📦 Using cached provider search data');
      return cached;
    }

    try {
      const response = await api.get('/customer/search/providers', { params });
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [FRONTEND API] Error searching providers:', error);
      throw error.response?.data || { error: 'Failed to search providers' };
    }
  },

  // Get provider details with caching
  getProvider: async (providerId) => {
    const cacheKey = `provider_details_${providerId}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      console.log('📦 Using cached provider details data');
      return cached;
    }

    try {
      console.log(`🔍 Fetching provider ${providerId} details from API...`);
      const response = await api.get(`/provider-routes/${providerId}`);
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching provider ${providerId} details:`, error);
      throw error.response?.data || { error: 'Failed to get provider details' };
    }
  },

  // Alias for getProvider to maintain compatibility
  getProviderDetails: async (providerId) => {
    return providerAPI.getProvider(providerId);
  },

  // Get provider services with caching
  getProviderServices: async (providerId, params = {}) => {
    const cacheKey = `provider_services_${providerId}_${JSON.stringify(params)}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      console.log('📦 Using cached provider services data');
      return cached;
    }

    try {
      console.log(`🔍 Fetching services for provider ${providerId}...`);
      const response = await api.get(`/provider-routes/${providerId}/services`, { params });
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching services for provider ${providerId}:`, error);
      throw error.response?.data || { error: 'Failed to fetch provider services' };
    }
  },

  // Get provider offers with caching
  getProviderOffers: async (providerId) => {
    const cacheKey = `provider_offers_${providerId}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      console.log('📦 Using cached provider offers data');
      return cached;
    }

    try {
      console.log(`🔍 Fetching offers for provider ${providerId}...`);
      const response = await api.get(`/provider-routes/${providerId}/offers`);
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching offers for provider ${providerId}:`, error);
      throw error.response?.data || { error: 'Failed to get provider offers' };
    }
  },

  // Get provider products with caching
  getProviderProducts: async (providerId, params = {}) => {
    const cacheKey = `provider_products_${providerId}_${JSON.stringify(params)}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      console.log('📦 Using cached provider products data');
      return cached;
    }

    try {
 //     console.log(`🔍 Fetching products for provider ${providerId}...`);
      const response = await api.get(`/provider-routes/${providerId}/products`, { params });
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching products for provider ${providerId}:`, error);
      throw error;
    }
  },

  // Get provider reviews with caching
  getProviderReviews: async (providerId, params = {}) => {
    const cacheKey = `provider_reviews_${providerId}_${JSON.stringify(params)}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
   //   console.log('📦 Using cached provider reviews data');
      return cached;
    }

    try {
 //     console.log(`🔍 Fetching reviews for provider ${providerId}...`);
      const response = await api.get(`/provider-routes/${providerId}/reviews`, { params });
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching reviews for provider ${providerId}:`, error);
      throw error;
    }
  },

  // Get ALL provider data in one call (details, services, reviews, photo gallery)
  getProviderAllData: async (providerId, locationParams = {}) => {
    const cacheKey = `provider_all_data_${providerId}_${JSON.stringify(locationParams)}`;
    
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const response = await api.get(`/provider-routes/${providerId}/allproviderdata`, { 
        params: { 
          ...locationParams, 
          _t: Date.now() // Add timestamp to prevent caching
        } 
      });
      
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching all data for provider ${providerId}:`, error);
      throw error.response?.data || { error: 'Failed to fetch provider all data' };
    }
  }
};

// Customer API methods
export const customerAPI = {
  // Get customer statistics
  getStatistics: async () => {
    try {
      const response = await api.get('/customer/profile/statistics');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get customer statistics' };
    }
  },
  // Get offers with enhanced caching
  getOffers: async (params = {}) => {
    const cacheKey = `customer_offers_${JSON.stringify(params)}`;
    const cached = getCachedResponse(cacheKey);
    if (cached) {
  //    console.log('📦 Using cached customer offers data');
      return cached;
    }

    try {
  //    console.log('🔍 Fetching customer offers from API...');
      const response = await api.get('/customer/offers', { params });
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer offers:', error);
      throw error.response?.data || { error: 'Failed to get offers' };
    }
  },

  // Get featured offers with caching
  getFeaturedOffers: async () => {
    const cacheKey = 'customer_featured_offers';
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      console.log('📦 Using cached featured offers data');
      return cached;
    }

    try {
   //   console.log('🔍 Fetching featured offers from API...');
      const response = await api.get('/customer/offers/featured');
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching featured offers:', error);
      throw error.response?.data || { error: 'Failed to get featured offers' };
    }
  },

  // Get trending offers with caching
  getTrendingOffers: async () => {
    const cacheKey = 'customer_trending_offers';
    const cached = getCachedResponse(cacheKey);
    if (cached) {
      console.log('📦 Using cached trending offers data');
      return cached;
    }

    try {
      console.log('🔍 Fetching trending offers from API...');
      const response = await api.get('/customer/offers/trending');
      setCachedResponse(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching trending offers:', error);
      throw error.response?.data || { error: 'Failed to get trending offers' };
    }
  },
};

// Cart API methods
export const cartAPI = {
  // Get customer cart
  getCustomerCart: async (customerId) => {
    try {
      const response = await api.get('/customer/cart');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get cart' };
    }
  },

  // Add to customer cart
  addToCustomerCart: async (customerId, productId, quantity, serviceId = null, itemType = 'product', providerId = 'store', providerName = null, comboFields = {}) => {
    console.log('🛒 [API SERVICE] addToCustomerCart called with:', { serviceId, comboFields });
    try {
      const requestBody = { quantity };
      
      if (productId) {
        requestBody.productId = productId;
        requestBody.itemType = 'product';
      } else if (serviceId) {
        requestBody.serviceId = serviceId;
        requestBody.itemType = 'service';
        requestBody.providerId = providerId;
        requestBody.providerName = providerName;
        
        // Add combo fields if provided
        console.log('🔍 [API DEBUG] Checking combo fields:', comboFields);
        if (comboFields.isCombo) {
          console.log('🔍 [API DEBUG] Adding combo fields to request body');
          requestBody.isCombo = comboFields.isCombo;
          requestBody.comboName = comboFields.comboName;
          requestBody.comboId = comboFields.comboId;
          requestBody.selectedServices = comboFields.selectedServices;
          requestBody.comboServiceNames = comboFields.comboServiceNames;
          requestBody.comboServiceDetails = comboFields.comboServiceDetails;
        } else {
       //   console.log('🔍 [API DEBUG] No combo fields to add');
        }
      }
      
      const response = await api.post('/customer/cart/add', requestBody);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to add to cart' };
    }
  },

  // Update cart item quantity
  updateCartItemQuantity: async (cartItemId, quantity) => {
    try {
      const response = await api.put(`/customer/cart/items/${cartItemId}`, { quantity });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to update cart item' };
    }
  },

  // Remove from cart
  removeFromCart: async (cartItemId) => {
    try {
      const response = await api.delete(`/customer/cart/items/${cartItemId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to remove from cart' };
    }
  },

  // Clear cart
  clearCart: async () => {
    try {
      const response = await api.delete('/customer/cart/clear');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to clear cart' };
    }
  },

  // Clear cart for specific provider
  clearProviderCart: async (providerId) => {
    try {
      const response = await api.delete(`/customer/cart/clear-provider/${providerId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to clear provider cart' };
    }
  }
};

// Wallet API methods
export const walletAPI = {
  // Get wallet balance
  getWalletBalance: async () => {
    try {
      const response = await api.get('/customer/wallet/balance');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get wallet balance' };
    }
  },

  // Get wallet transactions
  getWalletTransactions: async (params = {}) => {
    try {
      const response = await api.get('/customer/wallet/transactions', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get wallet transactions' };
    }
  },

  // Add money to wallet
  addMoneyToWallet: async (amount, paymentMethod) => {
    try {
      const response = await api.post('/customer/wallet/topup', { amount, paymentMethod });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to add money to wallet' };
    }
  }
};

// Payment API methods
export const paymentAPI = {
  // Get payment methods
  getPaymentMethods: async () => {
    try {
      const response = await api.get('/customer/payment-methods');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get payment methods' };
    }
  },

  // Add payment method
  addPaymentMethod: async (paymentData) => {
    try {
      const response = await api.post('/customer/payment-methods', paymentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to add payment method' };
    }
  },

  // Remove payment method
  removePaymentMethod: async (paymentMethodId) => {
    try {
      const response = await api.delete(`/customer/payment-methods/${paymentMethodId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to remove payment method' };
    }
  },

  // Process payment
  processPayment: async (paymentData) => {
    try {
      const response = await api.post('/customer/payments/process', paymentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to process payment' };
    }
  }
};

// Product API methods
export const productAPI = {
  // Get products
  getProducts: async (params = {}) => {
    try {
      const response = await api.get('/customer/products', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get products' };
    }
  },

  // Get product details
  getProductDetails: async (productId) => {
    try {
      const response = await api.get(`/customer/products/${productId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get product details' };
    }
  },

  // Search products
  searchProducts: async (query, params = {}) => {
    try {
      const response = await api.get('/customer/products/search', { params: { query, ...params } });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to search products' };
    }
  }
};

// Chat API methods
export const chatAPI = {
  // Test function to verify export is working
  testFunction: () => {
    console.log('🧪 chatAPI testFunction called - export is working!');
    return 'chatAPI export working';
  },
  
  // Get chats (conversations list)
  getChats: async () => {
    try {
      const response = await api.get('/customer/chat');
      return {
        success: true,
        data: {
          chats: response.data.chats || []
        }
      };
    } catch (error) {
      console.error('Error fetching chats:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to load chats'
      };
    }
  },

  // Get total unread message count
  getUnreadCount: async () => {
    try {
      const response = await api.get('/customer/chat/unread-count');
      if (response.data.success) {
        return {
          success: true,
          data: {
            unreadCount: response.data.data.unreadCount
          }
        };
      } else {
        return {
          success: false,
          error: 'Failed to get unread count'
        };
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to load unread count'
      };
    }
  },

  // Get chat messages
  getChatMessages: async (chatId) => {
    try {
   //   console.log('🌐 [API] Calling getChatMessages with chatId:', chatId);
  //    console.log('🌐 [API] Full URL:', `/customer/chat/${chatId}/messages`);
      // Add cache-busting parameter to ensure fresh data
      const cacheBuster = Date.now();
      const response = await api.get(`/customer/chat/${chatId}/messages?t=${cacheBuster}`);
 //     console.log('🌐 [API] getChatMessages response:', response.data);
 //     console.log('🌐 [API] Response status:', response.status);
 //     console.log('🌐 [API] Response headers:', response.headers);
//      console.log('🌐 [API] Raw response:', response);
      return {
        success: true,
        data: {
          messages: response.data.messages || [],
          chat: response.data.chat || null
        }
      };
    } catch (error) {
      console.error('🌐 [API] Error fetching messages:', error);
      console.error('🌐 [API] Error response:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to load messages'
      };
    }
  },

  // Send message
  sendMessage: async (chatId, messageData) => {
    try {
      const response = await api.post(`/customer/chat/${chatId}/messages`, messageData);
      return {
        success: true,
        data: {
          message: response.data.message || {
            id: Date.now(),
            content: messageData.content,
            timestamp: new Date().toISOString()
          }
        }
      };
    } catch (error) {
      console.error('Error sending message:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to send message'
      };
    }
  },

  // Mark messages as read
  markAsRead: async (chatId, messageIds) => {
    try {
      const response = await api.put(`/customer/chat/${chatId}/read`, { messageIds });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error marking messages as read:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to mark messages as read'
      };
    }
  },

  // Create or find chat with provider
  createChat: async (providerId, initialMessage = null) => {
    try {
      const response = await api.post('/customer/chat', {
        providerId,
        initialMessage
      });
      return {
        success: true,
        data: {
          chatId: response.data.data.chatId,
          provider: response.data.data.provider || null
        }
      };
    } catch (error) {
      console.error('Error creating chat:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create chat'
      };
    }
  },

  // Get provider information
  getProviderInfo: async (providerId) => {
    try {
      const response = await api.get(`/provider-routes/${providerId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching provider info:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch provider info'
      };
    }
  },

  // Get conversations (legacy method for compatibility)
  getConversations: async () => {
    try {
      const response = await api.get('/customer/chat/conversations');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get conversations' };
    }
  },

  // Get messages (legacy method for compatibility)
  getMessages: async (conversationId) => {
    try {
      const response = await api.get(`/customer/chat/conversations/${conversationId}/messages`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get messages' };
    }
  }
};

// Service API methods
export const serviceAPI = {
  // Get service details for a specific provider
  getServiceDetails: async (serviceId, providerId) => {
    try {
      // Use the provider-routes directly as mentioned by user
      const response = await api.get(`/provider-routes/${providerId}/services/${serviceId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get service details' };
    }
  }
};

// Service Booking API methods
export const serviceBookingAPI = {
  // Reserve slot
  reserveSlot: async (providerId, reservationData) => {
    try {
           
      const response = await api.post('/customer/availability/reserve', reservationData);
    
      
      
      // Check if the response has the expected format
      // Handle both direct format and wrapped format from successResponse
      const hasReservationKeys = response.data.reservationKeys || 
                                 response.data.data?.reservationKeys || 
                                 response.data.reservationKey || 
                                 response.data.data?.reservationKey;
      
      if (!hasReservationKeys) {
        console.error('🔒 [API] Response missing reservation keys:', response.data);
        alert(`API Response missing reservation keys: ${JSON.stringify(response.data)}`);
      }
      
       
      return response.data;
    } catch (error) {
      console.error('🔒 [API] Reservation request failed:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        timestamp: new Date().toISOString()
      });
      throw error.response?.data || { error: 'Failed to reserve slot' };
    }
  },

  // Release slot reservation
  releaseSlot: async (reservationId, customerId) => {
    try {
      const response = await api.delete(`/customer/availability/reserve/${reservationId}`, {
        data: { customerId }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to release slot' };
    }
  },

  // Confirm slot reservation (convert reservation to booking)
  confirmReservation: async (reservationId) => {
    try {
      const response = await api.post('/customer/slot-reservations/confirm', { reservationId });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to confirm reservation' };
    }
  },

  // Book service
  bookService: async (bookingData) => {
    try {
      const response = await api.post('/customer/bookings/services', bookingData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to book service' };
    }
  },

  // Get service bookings
  getServiceBookings: async (params = {}) => {
    try {
      const response = await api.get('/customer/bookings/services', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get service bookings' };
    }
  },

  // Cancel service booking
  cancelServiceBooking: async (bookingId) => {
    try {
      const response = await api.put(`/customer/bookings/services/${bookingId}/cancel`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to cancel service booking' };
    }
  },

  // Create service booking (for payment processing)
  createServiceBooking: async (bookingData) => {
    try {
      const response = await api.post('/customer/payments/process-direct', {
        amount: Math.round(bookingData.totalAmount * 100),
        currency: 'aud',
        orderType: 'service',
        bookingData: bookingData
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to create service booking' };
    }
  }
};

// Search API methods
export const searchAPI = {
  // Search providers (salons and professionals)
  searchProviders: async (query, params = {}) => {
    try {
      const response = await api.get('/customer/search/providers', { params: { query, ...params } });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to search providers' };
    }
  },

  // Search services
  searchServices: async (query, params = {}) => {
    try {
      const response = await api.get('/customer/search/services', { params: { query, ...params } });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to search services' };
    }
  },

  // Search products
  searchProducts: async (query, params = {}) => {
    try {
      const response = await api.get('/customer/search/products', { params: { query, ...params } });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to search products' };
    }
  },

  // Combined search (providers, services, products)
  combinedSearch: async (params = {}) => {
    try {
      // Allow calling with either a string (query) or an object of params
      const finalParams = typeof params === 'string' ? { query: params } : params;
      // Backend route is '/customer/search/all'
      const response = await api.get('/customer/search/all', { params: finalParams });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to perform combined search' };
    }
  },

  // Get search categories
  getSearchCategories: async () => {
    try {
      const response = await api.get('/customer/search/categories');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get search categories' };
    }
  }
};

// Booking API methods
export const bookingAPI = {
  // Get bookings
  getBookings: async (params = {}) => {
    try {
      const response = await api.get('/customer/bookings', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get bookings' };
    }
  },

  // Get booking details
  getBookingDetails: async (bookingId) => {
    try {
      const response = await api.get(`/customer/bookings/${bookingId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get booking details' };
    }
  },

  // Cancel booking
  cancelBooking: async (bookingId, cancelData) => {
    try {
      const response = await api.post(`/customer/bookings/${bookingId}/cancel`, cancelData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to cancel booking' };
    }
  },

  // Reschedule booking
  rescheduleBooking: async (bookingId, rescheduleData) => {
    try {
      const response = await api.post(`/customer/bookings/${bookingId}/reschedule`, rescheduleData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to reschedule booking' };
    }
  }
};

// Address API methods
export const addressAPI = {
  // Get addresses
  getAddresses: async () => {
    try {
      const response = await api.get('/customer/addresses');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get addresses' };
    }
  },

  // Add address
  addAddress: async (addressData) => {
    try {
      const response = await api.post('/customer/addresses', addressData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to add address' };
    }
  },

  // Update address
  updateAddress: async (addressId, addressData) => {
    try {
      const response = await api.put(`/customer/addresses/${addressId}`, addressData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to update address' };
    }
  },

  // Delete address
  deleteAddress: async (addressId) => {
    try {
      const response = await api.delete(`/customer/addresses/${addressId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to delete address' };
    }
  }
};

// Favorites API methods
export const favoritesAPI = {
  // Get customer favorites
  getFavorites: async (type = null) => {
    try {
      const url = type ? `/customer/favorites?type=${type}` : '/customer/favorites';
      console.log('🔍 [API] Getting favorites from:', url);
      const response = await api.get(url);
      console.log('🔍 [API] Favorites response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [API] Get favorites failed:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Failed to get favorites' };
    }
  },

  // Add to favorites
  addToFavorites: async (itemId, type) => {
    try {
    //  console.log('➕ [API] Adding to favorites:', { itemId, type });
      const response = await api.post('/customer/favorites/add', { itemId, type });
   //   console.log('➕ [API] Add to favorites response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [API] Add to favorites failed:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Failed to add to favorites' };
    }
  },

  // Remove from favorites
  removeFromFavorites: async (favoriteId) => {
    try {
      const response = await api.delete(`/customer/favorites/remove/${favoriteId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to remove from favorites' };
    }
  },

  // Check if item is in favorites
  checkFavoriteStatus: async (itemId, type) => {
    try {
   //   console.log('🔍 [API] Checking favorite status:', `/customer/favorites/check/${itemId}/${type}`);
      const response = await api.get(`/customer/favorites/check/${itemId}/${type}`);
   //   console.log('🔍 [API] Favorite status response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [API] Favorite status check failed:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Failed to check favorite status' };
    }
  },

  // Get favorites count
  getFavoritesCount: async (type = null) => {
    try {
      const url = type ? `/customer/favorites/count?type=${type}` : '/customer/favorites/count';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get favorites count' };
    }
  }
};

// Referral API methods
export const referralAPI = {
  // Get referral information
  getReferralInfo: async () => {
    try {
      const response = await api.get('/customer/referral/info');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get referral info' };
    }
  },

  // Get referral history
  getReferralHistory: async (params = {}) => {
    try {
      const response = await api.get('/customer/referral/history', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get referral history' };
    }
  },

  // Get referral analytics
  getReferralAnalytics: async () => {
    try {
      const response = await api.get('/customer/referral/analytics');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get referral analytics' };
    }
  },

  // Generate new referral code
  generateReferralCode: async () => {
    try {
      const response = await api.post('/customer/referral/generate-code');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to generate referral code' };
    }
  },

  // Share referral via email
  shareReferral: async (data) => {
    try {
      const response = await api.post('/customer/referral/share', data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to share referral' };
    }
  },

  // Get referral settings
  getReferralSettings: async () => {
    try {
      const response = await api.get('/customer/referral/settings');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get referral settings' };
    }
  },

  // Validate referral code (public endpoint)
  validateReferralCode: async (referralCode) => {
    try {
      const response = await api.get(`/customer/referral/validate/${referralCode}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to validate referral code' };
    }
  }
};

// Order API methods
export const orderAPI = {
  // Get orders
  getOrders: async (params = {}) => {
    try {
      const response = await api.get('/customer/order', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get orders' };
    }
  },

  // Get order details
  getOrderDetails: async (orderId) => {
    try {
      const response = await api.get(`/customer/order/${orderId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get order details' };
    }
  },

  // Cancel order
  cancelOrder: async (orderId) => {
    try {
      const response = await api.post(`/customer/order/${orderId}/cancel`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to cancel order' };
    }
  }
};

// Notification API methods
export const notificationAPI = {
  // Get notifications
  getNotifications: async (params = {}) => {
    try {
      const response = await api.get('/customer/notifications', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get notifications' };
    }
  },

  // Mark notification as read
  markNotificationAsRead: async (notificationId) => {
    try {
      const response = await api.put(`/customer/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to mark notification as read' };
    }
  },

  // Mark all notifications as read
  markAllNotificationsAsRead: async () => {
    try {
      const response = await api.put('/customer/notifications/read-all');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to mark all notifications as read' };
    }
  },

  // Send notification to provider using template
  sendNotificationToProvider: async (providerId, templateName, variables = {}) => {
    try {
      const response = await api.post('/customer/notifications/send-to-provider', {
        providerId,
        templateName,
        variables,
        redirectScreen: 'reviews' // Redirect to reviews page in service provider app
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to send notification to provider' };
    }
  }
};

// Location API methods

export const locationAPI = {
  // Get user location
  getUserLocation: async () => {
    try {
      const response = await api.get('/customer/location');
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get user location' };
    }
  },

  // Update user location
  updateUserLocation: async (locationData) => {
    try {
      const response = await api.put('/customer/location', locationData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to update user location' };
    }
  }
};

// Review API methods
export const reviewAPI = {
  // Submit a new review
  submitReview: async (reviewData) => {
    try {
      const response = await api.post('/customer/reviews', reviewData);
      return response;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to submit review' };
    }
  },

  // Get customer's reviews
  getMyReviews: async (params = {}) => {
    try {
      const response = await api.get('/customer/reviews/my', { params });
   
      return response;
    } catch (error) {
      console.error('❌ [REVIEW API] getMyReviews error:', error);
      throw error.response?.data || { error: 'Failed to get reviews' };
    }
  },

  // Update a review
  updateReview: async (reviewId, reviewData) => {
    try {
      const response = await api.put(`/customer/reviews/${reviewId}`, reviewData);
      return response;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to update review' };
    }
  },

  // Delete a review
  deleteReview: async (reviewId) => {
    try {
      const response = await api.delete(`/customer/reviews/${reviewId}`);
      return response;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to delete review' };
    }
  },

  // Get reviews for a provider
  getProviderReviews: async (providerId, params = {}) => {
    try {
      const response = await api.get(`/customer/reviews/provider/${providerId}`, { params });
      return response;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get provider reviews' };
    }
  },

  // Check if booking has been reviewed
  checkBookingReview: async (bookingId) => {
    try {
      const response = await api.get('/customer/reviews/my');
      const reviews = response.data.data?.reviews || [];
      return reviews.find(r => r.bookingId?._id === bookingId || r.bookingId === bookingId);
    } catch (error) {
      console.error('Error checking review status:', error);
      return null;
    }
  },

  // Get review statistics
  getReviewStats: async () => {
    try {
      const response = await api.get('/customer/reviews/stats');
      return response;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to get review statistics' };
    }
  }
};

// Export the main API instance for custom requests
export default api;
